import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

/**
 * Currency Context — global FX state.
 *
 * Resolution chain (mirrors backend.currency.resolve_currency):
 *   1. localStorage override (in-app toggle persists across reloads pre-login)
 *   2. user.preferred_currency (saved profile setting; pulled from /auth/me)
 *   3. GeoIP via /api/currency/info (server resolves from CF-IPCountry)
 *   4. USD default
 *
 * Setting a new currency:
 *   - Always writes to localStorage immediately (instant UI feedback).
 *   - If the user is logged in, also PUTs to /api/currency/preference so the
 *     choice survives logouts and follows them to any device.
 *   - Fetches fresh /currency/info to update the active symbol + rate.
 */
const CurrencyContext = createContext({
  currency: "USD",
  symbol: "$",
  decimals: 2,
  rate: 1.0,
  supported: [],
  loading: true,
  setCurrency: () => {},
  formatLocal: (amount) => `$${Number(amount || 0).toFixed(2)}`,
});

const STORAGE_KEY = "adrigo_currency_override";

export function CurrencyProvider({ children, user }) {
  const [state, setState] = useState({
    currency: "USD", symbol: "$", decimals: 2, rate: 1.0,
    source: "default", name: "US Dollar", supported: [],
    rates_source: null, rates_age_seconds: null,
    loading: true,
  });

  const fetchInfo = useCallback(async (override) => {
    try {
      const params = {};
      if (override) params.currency = override;
      const { data } = await api.get("/currency/info", { params });
      setState({
        currency: data.currency,
        symbol: data.symbol,
        decimals: data.decimals ?? 2,
        rate: data.rate_from_usd ?? 1.0,
        source: data.source,
        name: data.name,
        supported: data.supported || [],
        rates_source: data.rates_source || null,
        rates_age_seconds: typeof data.rates_age_seconds === "number" ? data.rates_age_seconds : null,
        loading: false,
      });
    } catch {
      // Silent fallback — never block the UI on FX failure.
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    // Honour explicit override from localStorage first; otherwise let the
    // server resolve via profile preference / GeoIP / default.
    const ov = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    fetchInfo(ov || user?.preferred_currency);
  }, [fetchInfo, user?.preferred_currency]);

  const setCurrency = useCallback(async (next) => {
    if (!next) return;
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch {}
    // Persist to profile if logged-in (best-effort).
    if (user?.id) {
      try { await api.put("/currency/preference", { currency: next }); } catch {}
    }
    await fetchInfo(next);
  }, [user?.id, fetchInfo]);

  const formatLocal = useCallback((amount) => {
    const n = Number(amount || 0);
    const d = state.decimals ?? 2;
    return `${state.symbol}${n.toLocaleString("en", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
  }, [state.symbol, state.decimals]);

  const value = useMemo(() => ({ ...state, setCurrency, formatLocal }), [state, setCurrency, formatLocal]);
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
